/* eslint-disable max-statements */
import { useCurrencies } from '@shared/contexts/currencies-context';
import { useOffers } from '@shared/contexts/offers';
import { DashboardApi } from '@shared/services/dashboard-api';
import { NeonApi } from '@shared/services/neon-api';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { OfferCheckout as OfferCheckoutComponent } from 'components/offerCheckout/offerCheckout';
import { FC, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader } from '../../components/loader';
import { RouterPathes } from '../../constants/router-paths';
import { url } from '../../constants/urls';

export const OfferCheckout: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedCurrency, selectedCountryInfo } = useCurrencies();
  const checkoutId = searchParams.get('checkoutId');

  const { data: neonPayOrder } = useQuery({
    queryKey: ['neonPayOrder', checkoutId],
    queryFn: () => NeonApi.getOrder(checkoutId as string),
    enabled: !!checkoutId,
  });

  useEffect(() => {
    if (!neonPayOrder) {
      return;
    }

    const checkoutOfferType = searchParams.get('type') === 'guild' ? 'guild' : 'regular';
    const params = new URLSearchParams({
      orderId: neonPayOrder.orderId,
      offerId: neonPayOrder.offerId,

      // Neon order response does not include offer type, so preserve current checkout type.
      type: checkoutOfferType,
      currency: neonPayOrder.paymentType,
      success: 'true',
    });

    navigate({
      pathname: RouterPathes.offerCheckout,
      search: params.toString(),
    });
  }, [neonPayOrder, navigate, searchParams]);

  const {
    guildOffers,
    regularOffers: offers,
    refetchGuildOffers,
    refetchRegularOffers: refetchOffers,
    guildMembers,
    isOffersLoading,
    isGuildFounder,
  } = useOffers();

  const { data: userDetails, isLoading: isUserDetailsLoading } = useQuery({
    queryKey: ['userDetails'],
    queryFn: DashboardApi.getUserInfo,
    refetchOnWindowFocus: false,
    throwOnError: (error) => {
      if (error instanceof AxiosError && error.status === 401) {
        const returnLoginUrl = window.location.href;

        window.location.href = url.redirectLogin(returnLoginUrl);
      }

      return true;
    },
    retry: 0,
  });

  const currentOfferId = searchParams.get('offerId');
  const currentOfferType = searchParams.get('type');
  const isRegularType = currentOfferType === 'regular';
  const isGuildType = currentOfferType === 'guild';
  const offer = useMemo(() => {
    if (!currentOfferId) return undefined;

    if (isRegularType && offers.length) {
      return offers.find(({ id }) => id === currentOfferId);
    }

    if (isGuildType && guildOffers.length) {
      return guildOffers.find(({ id }) => id === currentOfferId);
    }

    return undefined;
  }, [offers, guildOffers, currentOfferId, isRegularType, isGuildType]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentOfferId, currentOfferType]);

  if (isOffersLoading || isUserDetailsLoading || checkoutId) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader
          width={80}
          height={80}
        />
      </div>
    );
  }

  if (!currentOfferId || !offer || !userDetails) {
    navigate(RouterPathes.home);

    // eslint-disable-next-line consistent-return
    return;
  }

  return (
    <OfferCheckoutComponent
      offer={offer}
      userDetails={userDetails}
      currentOfferType={currentOfferType as 'regular' | 'guild'}
      guildMembers={guildMembers}
      refetchGuildOffers={refetchGuildOffers}
      refetchOffers={refetchOffers}
      currency={selectedCurrency}
      selectedCountryInfo={selectedCountryInfo}
      isGuildFounder={isGuildFounder}
    />
  );
};
